import { forwardRef, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from 'react-bootstrap/Modal';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import Button from '../Button/Button';
import useLogin from '../../Features/Autenticacion/Hooks/useLogin';
import { ParametrosCalculadoraService } from '../../Services/ParametrosCalculadoraService';
import { IndicadorCalculadoraService } from '../../Services/IndicadorCalculadoraService';
import './ModalCalculadora.css';
import 'react-datepicker/dist/react-datepicker.css';

const VIA_EVENT_LIMIT = 3;
registerLocale('es', es);

const DatePickerPortal = ({ children }) => createPortal(children, document.body);

const formatDateToInput = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const toIsoFromDateInput = (dateInput, endOfDay = false) => {
	if (!dateInput) {
		return new Date().toISOString();
	}

	const [year, month, day] = dateInput.split('-').map(Number);
	const hours = endOfDay ? 23 : 0;
	const minutes = endOfDay ? 59 : 0;
	const seconds = endOfDay ? 59 : 0;
	const milliseconds = endOfDay ? 999 : 0;

	return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds)).toISOString();
};

const parseDateInputToDate = (dateInput) => {
	if (!dateInput) {
		return null;
	}

	const [year, month, day] = dateInput.split('-').map(Number);

	if (!year || !month || !day) {
		return null;
	}

	return new Date(year, month - 1, day);
};

const createDefaultFormValues = () => {
	const today = new Date();
	const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

	return {
		indicador: '',
		tipoProyeccion: 'sin-proyeccion',
		fechaInicio: formatDateToInput(firstDayOfCurrentMonth),
		fechaFin: formatDateToInput(today),
	};
};

const getNoProjectionDateRange = () => {
	const today = new Date();
	const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

	return {
		fechaInicio: formatDateToInput(firstDayOfCurrentMonth),
		fechaFin: formatDateToInput(today),
	};
};

const DEFAULT_PROJECTION_OPTIONS = [
	{ value: 'sin-proyeccion', label: 'Sin proyeccion' },
	{ value: 'con-proyeccion', label: 'Con proyeccion' },
];

const SPECIAL_SECONDARY_RESULT_LABEL_BY_INDICATOR_ID = Object.freeze({
	7: 'VIA',
});

const VIA_EVENT_FIELD_DEFINITIONS = Object.freeze([
	{ viaFieldKey: 'KM', etiqueta: 'KM', nombre: 'KM', tipoDato: 'decimal', modificable: true, requeridoCalculadora: true },
	{ viaFieldKey: 'SENTIDO', etiqueta: 'Sentido', nombre: 'SENTIDO', tipoDato: 'seleccion', modificable: true, requeridoCalculadora: true },
	{ viaFieldKey: 'FIA', etiqueta: 'FIA', nombre: 'FIA', tipoDato: 'decimal', modificable: true, requeridoCalculadora: true },
	{ viaFieldKey: 'FIM', etiqueta: 'FIM', nombre: 'FIM', tipoDato: 'decimal', modificable: true, requeridoCalculadora: true },
]);

const EMPTY_INITIAL_VALUES = {};

const createDefaultCalculationResult = () => ({
	idIndicador: null,
	indicador: '',
	resultado: null,
	valorIndicador: null,
	valorIndicador2: null,
	valorCumplimiento: null,
	conProyeccion: false,
	datosUsados: [],
	requestCompleted: false,
});

const createDefaultCalculationInfo = () => ({
	indicador: '',
	unidadMedicion: '',
	calculoOperativo: '',
});

const sortByOrder = (items = []) => {
	return [...items].sort((left, right) => Number(left?.orden ?? 0) - Number(right?.orden ?? 0));
};

const normalizeText = (value) => (
	String(value ?? '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim()
);

const isViaIndicator = (indicatorId) => (
	SPECIAL_SECONDARY_RESULT_LABEL_BY_INDICATOR_ID[Number(indicatorId)] === 'VIA'
);

const resolveViaFieldKeys = (parameter) => {
	const normalizedName = normalizeText(parameter?.nombre);
	const normalizedLabel = normalizeText(parameter?.etiqueta);
	const normalizedValue = `${normalizedName} ${normalizedLabel}`.replace(/[^a-z0-9]+/g, ' ');
	const tokens = normalizedValue.split(/\s+/).filter(Boolean);
	const fieldKeys = [];

	if (tokens.includes('km')) {
		fieldKeys.push('KM');
	}

	if (tokens.includes('fia')) {
		fieldKeys.push('FIA');
	}

	if (tokens.includes('fim')) {
		fieldKeys.push('FIM');
	}

	return fieldKeys;
};

const createEventFieldName = (baseKey, index) => (
	index === 0 ? baseKey : `${baseKey}_${index + 1}`
);

const getVisibleParameterFields = (fields = []) => (
	fields.filter((parameter) => parameter.visible !== false)
);

const splitViaParameterFields = (fields = []) => {
	const visibleFields = getVisibleParameterFields(fields);
	const viaEventFieldsByKey = new Map();
	const regularFields = [];

	visibleFields.forEach((parameter) => {
		const viaFieldKeys = resolveViaFieldKeys(parameter);

		if (viaFieldKeys.length > 0) {
			viaFieldKeys.forEach((viaFieldKey) => {
				if (!viaEventFieldsByKey.has(viaFieldKey)) {
					viaEventFieldsByKey.set(viaFieldKey, {
						...parameter,
						viaFieldKey,
					});
				}
			});
			return;
		}

		regularFields.push(parameter);
	});

	const orderedViaEventFields = ['KM', 'FIA', 'FIM']
		.map((fieldKey) => viaEventFieldsByKey.get(fieldKey))
		.filter(Boolean);

	return {
		regularFields,
		viaEventFields: orderedViaEventFields,
	};
};

const resolveViaEventFields = (fields = []) => {
	const viaEventFieldsByKey = new Map(
		fields.map((field) => [field.viaFieldKey, field]),
	);

	return VIA_EVENT_FIELD_DEFINITIONS.map((definition) => (
		viaEventFieldsByKey.get(definition.viaFieldKey) ?? definition
	));
};

const resolveCalculationInfoContent = (calculationInfo) => {
	if (!calculationInfo) {
		return null;
	}

	const indicatorName = String(calculationInfo.indicador ?? '').trim();
	const indicatorDetail = String(calculationInfo.unidadMedicion ?? '').trim();
	const description = String(calculationInfo.calculoOperativo ?? '').trim();

	if (!indicatorName && !indicatorDetail && !description) {
		return null;
	}

	return {
		title: 'Detalle de cálculo operativo',
		indicatorName,
		indicatorDetail,
		description,
	};
};

const InfoDocumentIcon = () => (
	<svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 22 22' fill='none'>
		<path d='M7.10938 1.77777V5.33333M10.6649 1.77777V5.33333M14.2205 1.77777V5.33333' stroke='white' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		<path d='M15.9991 3.55554H5.33247C4.35063 3.55554 3.55469 4.35148 3.55469 5.33332V17.7778C3.55469 18.7596 4.35063 19.5555 5.33247 19.5555H15.9991C16.981 19.5555 17.7769 18.7596 17.7769 17.7778V5.33332C17.7769 4.35148 16.981 3.55554 15.9991 3.55554Z' stroke='white' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		<path d='M7.10938 8.88889H12.4427M7.10938 12.4444H14.2205M7.10938 16H11.5538' stroke='white' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
	</svg>
);

const ViaRemoveIcon = () => (
	<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
		<path d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z' stroke='#FC0000' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
		<path d='M8 12H16' stroke='#FC0000' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
	</svg>
);

const ViaAddIcon = () => (
	<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
		<path d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z' stroke='#49B461' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
		<path d='M12 8V16' stroke='#49B461' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
		<path d='M8 12H16' stroke='#49B461' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
	</svg>
);

const CalculatorDateInput = forwardRef((props, ref) => (
	<input
		id={props.id}
		name={props.name}
		className='form-control'
		value={props.value}
		onClick={props.onClick}
		readOnly
		ref={ref}
		placeholder={props.placeholder}
		required={props.required}
	/>
));

CalculatorDateInput.displayName = 'CalculatorDateInput';

const ModalCalculadora = ({
	show,
	onHide,
	title = '',
	subtitle = '',
	resultTitle = 'RESULTADOS',
	indicatorLabel = 'Indicadores',
	indicatorPlaceholder = 'Escribe o seleccionar indicador',
	indicatorOptions = [],
	projectionOptions = DEFAULT_PROJECTION_OPTIONS,
	initialValues = EMPTY_INITIAL_VALUES,
	onClear,
	showCloseButton = false,
	showIntro = false,
	isFloating = false,
	floatingClassName = '',
}) => {
	const { user, usuario } = useLogin();
	const defaultFormValues = useMemo(() => createDefaultFormValues(), []);
 	const userId = user?.id || usuario?.id || 0;

	const resolvedInitialValues = useMemo(() => ({
		...defaultFormValues,
		...(initialValues || {}),
		indicador: (initialValues || {}).indicador ?? '',
	}), [defaultFormValues, initialValues]);

	const [formValues, setFormValues] = useState(resolvedInitialValues);
	const [parameterFields, setParameterFields] = useState([]);
	const [parameterValues, setParameterValues] = useState({});
	const [parameterMeta, setParameterMeta] = useState({
		fechaDesdeObligatoria: false,
		fechaHastaObligatoria: false,
	});
	const [calculationInfo, setCalculationInfo] = useState(createDefaultCalculationInfo());
	const [isLoadingParameters, setIsLoadingParameters] = useState(false);
	const [parametersRefreshKey, setParametersRefreshKey] = useState(0);
	const [calculationResult, setCalculationResult] = useState(createDefaultCalculationResult());
	const [isCalculating, setIsCalculating] = useState(false);
	const [hasCalculated, setHasCalculated] = useState(false);
	const [showInfoModal, setShowInfoModal] = useState(false);
	const [viaEventCount, setViaEventCount] = useState(1);
	const [viaSentidos, setViaSentidos] = useState([]);
	const specialSecondaryResultLabel = SPECIAL_SECONDARY_RESULT_LABEL_BY_INDICATOR_ID[Number(calculationResult.idIndicador)] ?? null;
	const calculationInfoContent = useMemo(() => (
		resolveCalculationInfoContent(calculationInfo)
	), [calculationInfo]);
	const isViaSelected = useMemo(() => (
		isViaIndicator(formValues.indicador)
	), [formValues.indicador]);
	const { regularFields, viaEventFields } = useMemo(() => (
		splitViaParameterFields(parameterFields)
	), [parameterFields]);
	const viaEventDisplayFields = useMemo(() => (
		resolveViaEventFields(viaEventFields)
	), [viaEventFields]);
	const showViaEventFields = isViaSelected && formValues.tipoProyeccion === 'con-proyeccion';

	useEffect(() => {
		setFormValues(resolvedInitialValues);
	}, [resolvedInitialValues, show]);

	useEffect(() => {
		if (!show) {
			setFormValues(resolvedInitialValues);
			setParameterFields([]);
			setParameterValues({});
			setParameterMeta({
				fechaDesdeObligatoria: false,
				fechaHastaObligatoria: false,
			});
			setCalculationInfo(createDefaultCalculationInfo());
			setCalculationResult(createDefaultCalculationResult());
			setHasCalculated(false);
			setShowInfoModal(false);
			setViaEventCount(1);
			setViaSentidos([]);
		}
	}, [show]);

	useEffect(() => {
		if (!show || !userId || !formValues.indicador) {
			setParameterFields([]);
			setParameterValues({});
			setParameterMeta({
				fechaDesdeObligatoria: false,
				fechaHastaObligatoria: false,
			});
			setCalculationInfo(createDefaultCalculationInfo());
			setViaSentidos([]);
			return;
		}

		let isMounted = true;

		const fetchParametros = async () => {
			setParameterFields([]);
			setParameterValues({});
			setIsLoadingParameters(true);

			const shouldUseNoProjectionDates = formValues.tipoProyeccion === 'sin-proyeccion';
			const fallbackDates = getNoProjectionDateRange();
			const fechaInicio = shouldUseNoProjectionDates ? fallbackDates.fechaInicio : formValues.fechaInicio;
			const fechaFin = shouldUseNoProjectionDates ? fallbackDates.fechaFin : formValues.fechaFin;

			const response = await ParametrosCalculadoraService({
				idUsuario: Number(userId),
				idIndicador: Number(formValues.indicador),
				fechaDesde: toIsoFromDateInput(fechaInicio),
				fechaHasta: toIsoFromDateInput(fechaFin, true),
				conProyeccion: formValues.tipoProyeccion === 'con-proyeccion',
			});

			if (!isMounted) {
				return;
			}

			if (response && response.status === 200 && response.data) {
				const parametros = sortByOrder(Array.isArray(response.data.parametros) ? response.data.parametros : []);
				setParameterFields(parametros);
				setParameterMeta({
					fechaDesdeObligatoria: Boolean(response.data.fechaDesdeObligatoria),
					fechaHastaObligatoria: Boolean(response.data.fechaHastaObligatoria),
				});
				setCalculationInfo({
					indicador: response.data.indicador ?? '',
					unidadMedicion: response.data.unidadMedicion ?? '',
					calculoOperativo: response.data.calculoOperativo ?? '',
				});
				setViaSentidos(Array.isArray(response.data.sentidosVia) ? response.data.sentidosVia : []);

				const nextParameterValues = parametros.reduce((acc, item) => {
					acc[item.nombre] = item.valor ?? '';
					return acc;
				}, {});

				setParameterValues(nextParameterValues);
				setViaEventCount(1);
			} else {
				setParameterFields([]);
				setParameterValues({});
				setCalculationInfo(createDefaultCalculationInfo());
				setViaEventCount(1);
				setViaSentidos([]);
			}

			setIsLoadingParameters(false);
		};

		fetchParametros();

		return () => {
			isMounted = false;
		};
	}, [
		formValues.fechaFin,
		formValues.fechaInicio,
		formValues.indicador,
		formValues.tipoProyeccion,
		parametersRefreshKey,
		show,
		userId,
	]);

	const handleClose = () => {
		setFormValues(resolvedInitialValues);
		setParameterFields([]);
		setParameterValues({});
		setParameterMeta({
			fechaDesdeObligatoria: false,
			fechaHastaObligatoria: false,
		});
		setCalculationInfo(createDefaultCalculationInfo());
		setCalculationResult(createDefaultCalculationResult());
		setHasCalculated(false);
		setShowInfoModal(false);
		setViaEventCount(1);
		setViaSentidos([]);
		onHide?.();
	};

	const handleFieldChange = ({ target: { name, value } }) => {
		if (name === 'indicador' || name === 'tipoProyeccion') {
			setCalculationResult(createDefaultCalculationResult());
			setHasCalculated(false);
		}

		if (name === 'tipoProyeccion') {
			const defaults = createDefaultFormValues();
			setFormValues((previousState) => ({
				...previousState,
				[name]: value,
				fechaInicio: defaults.fechaInicio,
				fechaFin: defaults.fechaFin,
			}));
		} else {
			setFormValues((previousState) => ({
				...previousState,
				[name]: value,
			}));
		}

		if (name === 'indicador') {
			setViaEventCount(1);
		}
	};

	const handleDateChange = (fieldName, date) => {
		if (!date) {
			return;
		}

		setCalculationResult(createDefaultCalculationResult());
		setHasCalculated(false);
		setFormValues((previousState) => ({
			...previousState,
			[fieldName]: formatDateToInput(date),
		}));
	};

	const handleParameterChange = ({ target: { name, value } }) => {
		if (value !== '' && Number(value) < 0) {
			return;
		}
		setParameterValues((previousState) => ({
			...previousState,
			[name]: value,
		}));
	};

	const handleCalculate = async () => {
		if (!userId || !formValues.indicador) {
			return;
		}

		const isViaCalculation = isViaIndicator(formValues.indicador);
		const hasProjection = formValues.tipoProyeccion === 'con-proyeccion';
		const shouldUseNoProjectionDates = formValues.tipoProyeccion === 'sin-proyeccion';
		const fallbackDates = getNoProjectionDateRange();
		const fechaInicio = shouldUseNoProjectionDates ? fallbackDates.fechaInicio : formValues.fechaInicio;
		const fechaFin = shouldUseNoProjectionDates ? fallbackDates.fechaFin : formValues.fechaFin;

		const requiredParameters = (
			isViaCalculation ? regularFields : parameterFields
		).reduce((acc, parameter) => {
			if (parameter?.requeridoCalculadora) {
				acc[parameter.nombre] = parameterValues[parameter.nombre];
			}

			return acc;
		}, {});

		const payload = {
			idUsuario: Number(userId),
			idIndicador: Number(formValues.indicador),
			fechaDesde: toIsoFromDateInput(fechaInicio),
			fechaHasta: toIsoFromDateInput(fechaFin, true),
			conProyeccion: hasProjection,
		};

		if (isViaCalculation) {
			if (Object.keys(requiredParameters).length > 0) {
				payload.parametros = requiredParameters;
			}

			if (showViaEventFields) {
				const viaEvents = Array.from({ length: viaEventCount }, (_, index) => {
					const kmValue = parameterValues[createEventFieldName('KM', index)];
					const sentidoValue = parameterValues[createEventFieldName('SENTIDO', index)];
					const fiaValue = parameterValues[createEventFieldName('FIA', index)];
					const fimValue = parameterValues[createEventFieldName('FIM', index)];

					const hasAnyValue = [kmValue, sentidoValue, fiaValue, fimValue].some((value) => (
						String(value ?? '').trim() !== ''
					));

					if (!hasAnyValue) {
						return null;
					}

					return {
						idMacroubicacion: sentidoValue ? Number(sentidoValue) : null,
						km: kmValue,
						porcentajeFia: fiaValue,
						porcentajeFim: fimValue,
					};
				}).filter(Boolean);

				if (viaEvents.length > 0) {
					payload.parametrosViaConProyeccion = viaEvents;
				}
			}
		} else {
			payload.parametros = requiredParameters;
		}

		setIsCalculating(true);
		setHasCalculated(true);
		setCalculationResult(createDefaultCalculationResult());
		try {
			const response = await IndicadorCalculadoraService(payload);

			if (response && response.status === 200) {
				setCalculationResult({
					idIndicador: response.data?.idIndicador ?? Number(formValues.indicador) ?? null,
					indicador: response.data?.indicador ?? calculationInfo.indicador ?? '',
					resultado: response.data?.resultado ?? null,
					valorIndicador: response.data?.valorIndicador ?? null,
					valorIndicador2: response.data?.valorIndicador2 ?? null,
					valorCumplimiento: response.data?.valorCumplimiento ?? null,
					conProyeccion: Boolean(response.data?.conProyeccion),
					datosUsados: Array.isArray(response.data?.datosUsados) ? response.data.datosUsados : [],
					requestCompleted: true,
				});
			}
		} finally {
			setIsCalculating(false);
		}
	};

	const handleClear = () => {
		setFormValues({
			...resolvedInitialValues,
			indicador: '',
			tipoProyeccion: formValues.tipoProyeccion,
		});
		setParameterFields([]);
		setParameterValues({});
		setParameterMeta({
			fechaDesdeObligatoria: false,
			fechaHastaObligatoria: false,
		});
		setParametersRefreshKey((previousValue) => previousValue + 1);
		setCalculationInfo(createDefaultCalculationInfo());
		setCalculationResult(createDefaultCalculationResult());
		setHasCalculated(false);
		setViaEventCount(1);
		setViaSentidos([]);
		onClear?.();
	};

	const handleAddViaEvent = () => {
		setViaEventCount((previousState) => (
			Math.min(previousState + 1, VIA_EVENT_LIMIT)
		));
	};

	const handleRemoveViaEvent = (eventIndex) => {
		if (viaEventCount <= 1) {
			return;
		}

		setParameterValues((previousState) => {
			const nextValues = { ...previousState };
			const preservedRegularValues = {};

			regularFields.forEach((field) => {
				preservedRegularValues[field.nombre] = previousState[field.nombre] ?? '';
			});

			const remainingEvents = Array.from({ length: viaEventCount }, (_, index) => index)
				.filter((index) => index !== eventIndex)
				.map((index) => (
					viaEventDisplayFields.reduce((acc, field) => {
						const fieldName = createEventFieldName(field.viaFieldKey, index);
						acc[field.viaFieldKey] = previousState[fieldName] ?? '';
						return acc;
					}, {})
				));

			Object.keys(nextValues).forEach((key) => {
				delete nextValues[key];
			});

			Object.entries(preservedRegularValues).forEach(([key, value]) => {
				nextValues[key] = value;
			});

			remainingEvents.forEach((eventValues, index) => {
				viaEventDisplayFields.forEach((field) => {
					const nextFieldName = createEventFieldName(field.viaFieldKey, index);
					nextValues[nextFieldName] = eventValues[field.viaFieldKey] ?? '';
				});
			});

			return nextValues;
		});

		setViaEventCount((previousState) => (
			Math.max(previousState - 1, 1)
		));
	};

	const shouldShowViaRemove = (index) => {
		if (viaEventCount === 1) {
			return false;
		}

		if (viaEventCount === VIA_EVENT_LIMIT) {
			return true;
		}

		return index < viaEventCount - 1;
	};

	const shouldShowViaAdd = (index) => (
		index === viaEventCount - 1 && viaEventCount < VIA_EVENT_LIMIT
	);

	const renderParameterInput = (
		parameter,
		fieldName = parameter.nombre,
		options = {},
	) => {
		const {
			forceEditable = false,
			hideHelperNote = false,
			wrapperClassName = '',
		} = options;
		const isInteger = parameter.tipoDato === 'entero';
		const isDecimal = parameter.tipoDato === 'decimal';
		const inputType = isInteger || isDecimal ? 'number' : 'text';
		const step = isDecimal ? '0.01' : '1';
		const shouldDisableInput = forceEditable ? false : !parameter.modificable;
		const shouldShowHelperNote = !hideHelperNote && !forceEditable && !parameter.modificable;

		return (
			<div key={fieldName} className={`modal-calculadora__field modal-calculadora__field--dynamic ${wrapperClassName}`.trim()}>
				<label className='modal-calculadora__field-label' htmlFor={`modal-calculadora-param-${fieldName}`}>
					{parameter.etiqueta}
				</label>
				<input
					id={`modal-calculadora-param-${fieldName}`}
					name={fieldName}
					type={inputType}
					step={step}
					min={inputType === 'number' ? '0' : undefined}
					className='modal-calculadora__control'
					value={parameterValues[fieldName] ?? ''}
					onChange={handleParameterChange}
					required={Boolean(parameter.requeridoCalculadora)}
					disabled={shouldDisableInput}
				/>
				{shouldShowHelperNote ? (
					<small className='modal-calculadora__dynamic-note'>Dato entregado por la base de datos</small>
				) : null}
			</div>
		);
	};

	const renderViaDirectionSelect = (index) => {
		const sentidoFieldName = createEventFieldName('SENTIDO', index);

		return (
			<div key={sentidoFieldName} className='modal-calculadora__field modal-calculadora__field--dynamic modal-calculadora__field--via'>
				<label className='modal-calculadora__field-label' htmlFor={`modal-calculadora-param-${sentidoFieldName}`}>
					Sentido
				</label>
				<select
					id={`modal-calculadora-param-${sentidoFieldName}`}
					name={sentidoFieldName}
					className='modal-calculadora__control modal-calculadora__control--select'
					value={parameterValues[sentidoFieldName] ?? ''}
					onChange={handleParameterChange}
					required
				>
					<option value=''>Seleccione</option>
					{viaSentidos.map((sentido) => (
						<option key={sentido.idMacroubicacion} value={sentido.idMacroubicacion}>
							{sentido.sentido}
						</option>
					))}
				</select>
			</div>
		);
	};

	const content = (
		<div className={`modal-calculadora${hasCalculated ? ' modal-calculadora--calculated' : ''}`}>
			<div className='modal-calculadora__results'>
				<div className='modal-calculadora__results-scroll'>
					<div className='modal-calculadora__results-header'>
						<p className='modal-calculadora__results-title'>{resultTitle}</p>
						{showCloseButton ? (
							<Button
								type='button'
								design='btn-outline-pewter-blue p-2 lh-1 modal-calculadora__close'
								onClick={handleClose}
							>
								<i className='ico-close'></i>
							</Button>
						) : null}
					</div>

					{hasCalculated ? (
						<>
							{specialSecondaryResultLabel ? (
								<>
									<div className='modal-calculadora__results-grid modal-calculadora__results-grid--summary'>
										<div className='modal-calculadora__result-card'>
											<span className='modal-calculadora__result-label'>VIA K</span>
											<strong className='modal-calculadora__result-value'>
												{calculationResult.valorIndicador ?? '--'}
											</strong>
										</div>
										<div className='modal-calculadora__result-card'>
											<span className='modal-calculadora__result-label'>VIA Total</span>
											<strong className='modal-calculadora__result-value'>
												{calculationResult.valorIndicador2 ?? '--'}
											</strong>
										</div>
									</div>
									<div className='modal-calculadora__results-grid modal-calculadora__results-grid--details'>
										<div className='modal-calculadora__result-card modal-calculadora__result-card--full-width'>
											<span className='modal-calculadora__result-label'>% cumplimiento</span>
											<strong className='modal-calculadora__result-value'>
												{calculationResult.valorCumplimiento ?? '--'}
											</strong>
										</div>
									</div>
								</>
							) : (
								<div className='modal-calculadora__results-grid modal-calculadora__results-grid--summary'>
									<div className='modal-calculadora__result-card'>
										<span className='modal-calculadora__result-label'>Valor Indicador</span>
										<strong className='modal-calculadora__result-value'>
											{calculationResult.valorIndicador ?? '--'}
										</strong>
									</div>
									<div className='modal-calculadora__result-card'>
										<span className='modal-calculadora__result-label'>% Cumplimiento</span>
										<strong className='modal-calculadora__result-value'>
											{calculationResult.valorCumplimiento ?? '--'}
										</strong>
									</div>
								</div>
							)}

							{!specialSecondaryResultLabel && calculationResult.datosUsados?.length > 0 ? (
								<div className='modal-calculadora__results-grid modal-calculadora__results-grid--details'>
									{calculationResult.datosUsados.map((item, index) => (
										<div key={item.nombre ?? item.etiqueta ?? index} className='modal-calculadora__result-card'>
											<span className='modal-calculadora__result-label'>{item.etiqueta ?? item.nombre}</span>
											<strong className='modal-calculadora__result-value'>
												{item.valor ?? '--'}
											</strong>
										</div>
									))}
								</div>
							) : null}

							{calculationResult.requestCompleted && calculationInfoContent ? (
								<button
									type='button'
									className='modal-calculadora__info-trigger'
									onClick={() => setShowInfoModal(true)}
									aria-label={`Ver detalle de calculo operativo de ${calculationInfoContent.indicatorName}`}
								>
									<InfoDocumentIcon />
								</button>
							) : null}
						</>
					) : null}
				</div>
			</div>

			<div className='modal-calculadora__form-shell'>
				<div className='modal-calculadora__form'>
					{showIntro ? (
						<div className='modal-calculadora__intro'>
							<h5 className='modal-calculadora__title'>{title}</h5>
							<p className='modal-calculadora__subtitle'>{subtitle}</p>
						</div>
					) : null}

					<div className='modal-calculadora__field'>
						<label className='modal-calculadora__field-label' htmlFor='modal-calculadora-indicador'>
							{indicatorLabel}
						</label>
						<select
							id='modal-calculadora-indicador'
							name='indicador'
							className='modal-calculadora__control modal-calculadora__control--select'
							value={formValues.indicador}
							onChange={handleFieldChange}
						>
							<option value=''>{indicatorPlaceholder}</option>
							{indicatorOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>

					<div className='modal-calculadora__projection-group' role='radiogroup' aria-label='Tipo de proyeccion'>
						{projectionOptions.map((option) => {
							const checked = formValues.tipoProyeccion === option.value;

							return (
								<label key={option.value} className='modal-calculadora__radio-option'>
									<input
										type='radio'
										name='tipoProyeccion'
										value={option.value}
										checked={checked}
										onChange={handleFieldChange}
									/>
									<span className='modal-calculadora__radio-indicator' aria-hidden='true' />
									<span>{option.label}</span>
								</label>
							);
						})}
					</div>

					{formValues.tipoProyeccion === 'con-proyeccion' ? (
						<div className='modal-calculadora__dates'>
							<div className='form-floating modal-calculadora__date-field'>
								<span className='datepicker-label'>Fecha inicio</span>
								<DatePicker
									id='modal-calculadora-fecha-inicio'
									locale='es'
									name='Fecha inicio'
									selected={parseDateInputToDate(formValues.fechaInicio)}
									onChange={(date) => handleDateChange('fechaInicio', date)}
									customInput={<CalculatorDateInput />}
									wrapperClassName='rangePicker modal-calculadora__datepicker'
									dateFormat='dd-MM-yyyy'
									placeholderText='Fecha inicio'
									maxDate={formValues.fechaFin ? parseDateInputToDate(formValues.fechaFin) : new Date()}
									required={parameterMeta.fechaDesdeObligatoria}
									popperContainer={DatePickerPortal}
								/>
							</div>

							<div className='form-floating modal-calculadora__date-field'>
								<span className='datepicker-label'>Fecha Fin</span>
								<DatePicker
									id='modal-calculadora-fecha-fin'
									locale='es'
									name='Fecha Fin'
									selected={parseDateInputToDate(formValues.fechaFin)}
									onChange={(date) => handleDateChange('fechaFin', date)}
									customInput={<CalculatorDateInput />}
									wrapperClassName='rangePicker modal-calculadora__datepicker'
									dateFormat='dd-MM-yyyy'
									placeholderText='Fecha Fin'
									minDate={formValues.fechaInicio ? parseDateInputToDate(formValues.fechaInicio) : null}
									maxDate={new Date()}
									required={parameterMeta.fechaHastaObligatoria}
									popperContainer={DatePickerPortal}
								/>
							</div>
						</div>
					) : null}

					{isLoadingParameters ? (
						<p className='modal-calculadora__dynamic-loading'>Cargando parametros...</p>
					) : null}

					{(regularFields.length > 0 || showViaEventFields) ? (
						<div className='modal-calculadora__dynamic-fields'>
							{regularFields.map((parameter) => renderParameterInput(parameter))}

							{showViaEventFields ? (
								<div className='modal-calculadora__via-fields'>
									{Array.from({ length: viaEventCount }, (_, index) => (
										<div key={`via-row-${index + 1}`} className='modal-calculadora__via-row'>
											{viaEventDisplayFields.map((field) => (
												field.viaFieldKey === 'SENTIDO'
													? renderViaDirectionSelect(index)
													: renderParameterInput(
													{
														...field,
														etiqueta: field.viaFieldKey,
														nombre: createEventFieldName(field.viaFieldKey, index),
													},
													createEventFieldName(field.viaFieldKey, index),
													{
														forceEditable: true,
														hideHelperNote: true,
														wrapperClassName: 'modal-calculadora__field--via',
													},
													)
											))}
											{shouldShowViaAdd(index) ? (
												<button
													type='button'
													className='modal-calculadora__via-add'
													onClick={handleAddViaEvent}
													disabled={viaEventCount >= VIA_EVENT_LIMIT}
													aria-label='Agregar evento VIA'
												>
													<ViaAddIcon />
												</button>
											) : null}
											{shouldShowViaRemove(index) ? (
												<button
													type='button'
													className='modal-calculadora__via-remove'
													onClick={() => handleRemoveViaEvent(index)}
													aria-label={`Eliminar evento VIA ${index + 1}`}
												>
													<ViaRemoveIcon />
												</button>
											) : null}
										</div>
									))}
								</div>
							) : null}
						</div>
					) : null}
				</div>

				<div className='modal-calculadora__actions'>
					<Button type='button' design='btn-outline-pewter-blue w-100' onClick={handleClear}>
						Limpiar
					</Button>
					<Button type='button' design='btn-primary w-100' onClick={handleCalculate} disabled={isCalculating}>
						Calcular
					</Button>
				</div>
			</div>
		</div>
	);

	const infoModal = (
		<Modal
			show={showInfoModal}
			onHide={() => setShowInfoModal(false)}
			centered
			backdropClassName='backdrop-dark'
			className='modal-calculadora-info-shell'
			contentClassName='modal-calculadora-info-content'
		>
			<Modal.Body className='modal-calculadora-info-body'>
				<div className='modal-calculadora-info'>
					<div className='modal-calculadora-info__header'>
						<h5 className='modal-calculadora-info__title'>{calculationInfoContent?.title}</h5>
						<Button
							type='button'
							design='btn-outline-pewter-blue p-2 lh-1 modal-calculadora-info__close'
							onClick={() => setShowInfoModal(false)}
						>
							<i className='ico-close'></i>
						</Button>
					</div>

					<div className='modal-calculadora-info__content'>
						<div className='modal-calculadora-info__indicator'>
							<h6 className='modal-calculadora-info__indicator-name'>{calculationInfoContent?.indicatorName}</h6>
							<p className='modal-calculadora-info__indicator-detail'>{calculationInfoContent?.indicatorDetail}</p>
						</div>
						<p className='modal-calculadora-info__description'>{calculationInfoContent?.description}</p>
					</div>

					<div className='modal-calculadora-info__actions'>
						<Button
							type='button'
							design='btn-outline-pewter-blue modal-calculadora-info__action modal-calculadora-info__action--secondary'
							onClick={() => setShowInfoModal(false)}
						>
							Volver
						</Button>
						<Button
							type='button'
							design='btn-primary modal-calculadora-info__action'
							onClick={() => setShowInfoModal(false)}
						>
							Entendido
						</Button>
					</div>
				</div>
			</Modal.Body>
		</Modal>
	);

	if (isFloating) {
		if (!show) {
			return null;
		}

		return (
			<>
				<div className={`modal-calculadora-floating ${floatingClassName}`.trim()}>
					{content}
				</div>
				{infoModal}
			</>
		);
	}

	return (
		<>
			<Modal
				show={show}
				onHide={handleClose}
				centered
				backdropClassName='backdrop-dark'
				className='modal-calculadora-shell'
				contentClassName='modal-calculadora-content'
			>
				<Modal.Body className='modal-calculadora-body'>
					{content}
				</Modal.Body>
			</Modal>
			{infoModal}
		</>
	);
};

export default ModalCalculadora;
